/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { UserStateService } from 'src/app/shared/services/user-state.service';
import { BreakpointService } from 'src/app/shared/services/breakpoints.service';
import { SubEventsService } from 'src/app/shared/services/data-api/sub-events.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { ScoreFocusedEventsService } from '../score-focused-events.service';
import {
  TemporalityWidths,
  computeTemporalityWidths,
  hasTemporalityAnalytics,
} from './chartmap-mosaic-shared';

/**
 * "Sentiment Events" mosaic extracted from chartmap-company. Owns the
 * sub-events list fetch (current + previous period for the trend bars).
 */
@Component({
  selector: 'app-sentiment-events-mosaic',
  templateUrl: './sentiment-events-mosaic.component.html',
  styles: [':host { display: contents; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SentimentEventsMosaicComponent implements OnChanges {
  /** Live URL params (display bindings). */
  @Input() qparam: any;
  /** Gated refresh trigger: set by the parent only when a refetch is due. */
  @Input() fetchParams: any;
  @Input() visible = false;
  /** Mosaic state maps shared with the parent (parent initializes per refresh). */
  @Input() nodata: any = {};
  @Input() loading: any = {};
  @Input() iready: any = {};

  events: any;
  orderedEvents: any[] = [];

  eventSeriesMap: Map<string, [number, number][]> = new Map();
  focusedTimeframe = '';
  screenSize$ = this.breakpointService.screenSize$;

  get maxCount(): number {
    if (!this.events?.length) return 0;
    return Math.max(0, ...this.events.map((event: any) => event.count ?? 0));
  }

  constructor(
    private router: Router,
    public userState: UserStateService,
    private breakpointService: BreakpointService,
    private chartmapSubEvents: SubEventsService,
    private scoreFocusedEventsService: ScoreFocusedEventsService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {
    this.scoreFocusedEventsService.eventSeriesMap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((seriesMap) => {
        this.eventSeriesMap = seriesMap;
        this.cdr.markForCheck();
      });
    this.scoreFocusedEventsService.timeframe$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tf) => {
        this.focusedTimeframe = tf ?? '';
        this.cdr.markForCheck();
      });
    this.destroyRef.onDestroy(() => this.cancel$.complete());
  }

  private lastFetchedParams: any;
  // Cancels the in-flight fetch on a new refresh or when the mosaic is hidden.
  private cancel$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    // Hidden (score is no longer sentiment): kill any in-flight fetch and clear
    // state so it doesn't flash stale data when it becomes visible again.
    if (changes['visible'] && !this.visible) {
      this.onHidden();
      return;
    }
    // Lazy: only fetch when the mosaic is actually visible (sentiment score).
    // Fetch on a new fetchParams cycle, or when it first becomes visible with
    // params already set — but never refetch the same params.
    if (
      (changes['fetchParams'] || changes['visible']) &&
      this.visible &&
      this.fetchParams &&
      this.fetchParams !== this.lastFetchedParams
    ) {
      this.lastFetchedParams = this.fetchParams;
      this.refresh();
    }
  }

  private onHidden(): void {
    this.cancel$.next();
    this.loading['Events'] = false;
    this.iready['Events'] = false;
    this.nodata['Events'] = false;
    this.lastFetchedParams = undefined;
    this.orderedEvents = [];
    this.cdr.markForCheck();
  }

  private orderEvents(events: any[]): any[] {
    if (!Array.isArray(events)) return [];
    const ordered = [...events].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
    const selectedId = this.qparam?.eventid;
    if (selectedId) {
      const idx = ordered.findIndex((item) => item.event?.id === selectedId);
      if (idx > 0) {
        ordered.unshift(ordered.splice(idx, 1)[0]);
      }
    }
    return ordered;
  }

  private refresh(): void {
    // Own the 'Events' loading state — the parent no longer pre-initializes it
    // (it's no longer a toggle option in `array_filter`).
    this.cancel$.next();
    this.loading['Events'] = true;
    this.nodata['Events'] = false;
    this.iready['Events'] = false;
    this.chartmapSubEvents
      .fetchSubEventsList(this.fetchParams)
      .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.events = result.events;
          this.orderedEvents = this.orderEvents(this.events);
          const nonEmpty = this.events.some((item) => item.count > 0);
          this.nodata['Events'] = false;
          if (nonEmpty) {
            this.iready['Events'] = true;
          } else {
            this.iready['Events'] = false;
            this.loading['Events'] = false;
          }
          this.cdr.markForCheck();

          const timeframe = this.qparam.time ? this.qparam.time : 7;
          const timeObj = DateUtils.fromTo(timeframe);
          const prevTime =
            moment(timeObj.dateFrom)
              .subtract(timeObj.period, 'days')
              .format('YYYY-MM-DD') +
            '--' +
            timeObj.dateFrom;

          this.chartmapSubEvents
            .fetchSubEventsList(this.qparam, prevTime)
            .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
            .subscribe((result2) => {
              const prevById = new Map<string, number>(
                result2.events.map((r) => [r.event.id, r.count]),
              );
              this.events.forEach((event, index) => {
                const prev = prevById.get(event.event.id);
                if (prev !== undefined) {
                  this.events[index].count_prev = prev;
                }
              });
              this.cdr.markForCheck();
            });
        },
        error: () => {
          this.nodata['Events'] = true;
          this.loading['Events'] = false;
          this.cdr.markForCheck();
        },
      });
  }

  /** click specific event */
  iEvent(item) {
    let eventid = null;
    let eventype = null;
    const newstype = this.qparam.newstype
      ? this.qparam.newstype
      : this.qparam.type &&
          !['industry', 'sector', 'supersector', 'subsector'].includes(
            this.qparam.type,
          )
        ? 'Events'
        : null;
    if (this.qparam.eventid !== item.event.id) {
      eventid = item.event.id;
      eventype = item.event.type;
    }
    this.router.navigate([], {
      queryParams: {
        eventid: eventid,
        eventype: eventype,
        newstype: newstype,
        newsday: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  // Memoized per analytics object (the chart replaces it wholesale) so rows get
  // stable references under OnPush and the math runs once per event, not per CD.
  private temporalityCache = new WeakMap<
    object,
    Map<string, TemporalityWidths>
  >();

  temporalityWidths(itemId: string): TemporalityWidths | null {
    const analytics = this.userState.scoreFocusedEventsAnalytics;
    if (!analytics) return null;
    let byId = this.temporalityCache.get(analytics);
    if (!byId) {
      byId = new Map();
      this.temporalityCache.set(analytics, byId);
    }
    let widths = byId.get(itemId);
    if (!widths) {
      widths = computeTemporalityWidths(analytics, itemId);
      byId.set(itemId, widths);
    }
    return widths;
  }

  get hasTemporalityAnalytics(): boolean {
    return hasTemporalityAnalytics(this.userState.scoreFocusedEventsAnalytics);
  }
}
